import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { EASE } from "./shared";

const INITIAL = { name: "", email: "", password: "" };

export function AuthDialog() {
  const { authOpen, setAuthOpen, login, register, loginWithGoogle } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState(INITIAL);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const close = () => setAuthOpen(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "register") await register(form);
      else await login({ email: form.email, password: form.password });
      toast.success(mode === "register" ? "Welcome to Radiance Realtors." : "Welcome back.");
      setForm(INITIAL);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not sign you in. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {authOpen && (
        <motion.div
          data-testid="auth-dialog"
          role="dialog" aria-modal="true" aria-labelledby="auth-dialog-title"
          className="fixed inset-0 z-[85] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={close}
        >
          <motion.form
            onSubmit={submit}
            initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="w-full max-w-md bg-[#111111] border border-[#D4AF37]/30 rounded-[2px] p-7"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-2">Customer Account</p>
                <h3 id="auth-dialog-title" className="font-serif-lux text-2xl text-[#F5F5F0]">{mode === "register" ? "Create Your Account" : "Sign In"}</h3>
                <p className="text-[12px] text-[#71717A] mt-1">Save and shortlist properties, then send them to an advisor.</p>
              </div>
              <button type="button" data-testid="auth-dialog-close" onClick={close} className="p-2 border border-[#D4AF37]/30 rounded-full text-[#F3E5AB]"><X size={14} /></button>
            </div>

            <button type="button" data-testid="auth-google-btn" onClick={loginWithGoogle} className="w-full py-3.5 text-[12px] font-semibold uppercase tracking-[0.2em] border border-[#D4AF37]/50 text-[#F3E5AB] hover:bg-[#D4AF37]/10 transition-colors rounded-[2px]">
              Continue with Google
            </button>
            <div className="flex items-center gap-3 my-5 text-[10px] uppercase tracking-[0.25em] text-[#52525b]">
              <span className="h-px flex-1 bg-[#D4AF37]/15" />or<span className="h-px flex-1 bg-[#D4AF37]/15" />
            </div>

            <div className="grid gap-3">
              {mode === "register" && <input data-testid="auth-name-input" className="lux-input" placeholder="Full Name" value={form.name} onChange={set("name")} required minLength={2} />}
              <input data-testid="auth-email-input" type="email" className="lux-input" placeholder="Email" value={form.email} onChange={set("email")} required />
              <input data-testid="auth-password-input" type="password" className="lux-input" placeholder={mode === "register" ? "Password (min 8 characters)" : "Password"} value={form.password} onChange={set("password")} required minLength={mode === "register" ? 8 : 1} />
            </div>
            <button data-testid="auth-submit-btn" disabled={busy} className="mt-5 w-full py-4 text-[12px] font-semibold uppercase tracking-[0.22em] bg-gradient-to-r from-[#C5A059] to-[#D4AF37] text-black hover:from-[#D4AF37] hover:to-[#F3E5AB] transition-colors rounded-[2px] disabled:opacity-50">
              {busy ? "Please wait..." : mode === "register" ? "Create Account" : "Sign In"}
            </button>
            <button type="button" data-testid="auth-switch-mode-btn" onClick={() => setMode(mode === "register" ? "login" : "register")} className="mt-4 w-full text-[11px] tracking-wider text-[#A1A1AA] hover:text-[#F3E5AB] transition-colors">
              {mode === "register" ? "Already have an account? Sign in" : "New here? Create an account"}
            </button>
          </motion.form>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
